import Cancel from '@mui/icons-material/Cancel';
import type { ChipProps, Palette, PaletteColor } from '@mui/material';
import { Chip, Typography, chipClasses, styled } from '@mui/material';

import { HStack } from '../Stack';

type LookoChipFilledProps = {
  /**
   * @default 'filled'
   */
  variant?: 'filled';

  /**
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'tertiary' | 'white' | 'red' | 'green' | 'purple' | 'gray';
};

type LookoChipOutlinedProps = {
  variant: 'outlined';

  /**
   * @default 'primary'
   */
  color?: 'primary' | 'secondary';
};

export type LookoChipBaseProps = {
  /**
   * 칩 텍스트.
   */
  label: React.ReactNode;

  /**
   * 삭제 아이콘을 보여줍니다.
   */
  showDeleteIcon?: boolean;

  /**
   * 값이 있으면 클릭 가능한 칩 형태가 됩니다.
   */
  onClick?: ChipProps['onClick'];

  /**
   * 비활성화 상태.
   */
  disabled?: ChipProps['disabled'];

  /**
   * 아이콘
   */
  icon?: ChipProps['icon'];
};

export type LookoChipProps = LookoChipBaseProps & (LookoChipFilledProps | LookoChipOutlinedProps);

const filledColorMap: Record<
  NonNullable<LookoChipFilledProps['color']>,
  {
    color: NonNullable<ChipProps['color']>;
    deleteIcon: {
      opacity: number;
    };
  }
> = {
  primary: {
    color: 'primary',
    deleteIcon: {
      opacity: 0.7,
    },
  },
  secondary: {
    color: 'chip.secondary',
    deleteIcon: {
      opacity: 0.7,
    },
  },
  tertiary: {
    color: 'chip.tertiary',
    deleteIcon: {
      opacity: 0.26,
    },
  },
  white: {
    color: 'chip.white',
    deleteIcon: {
      opacity: 0.7,
    },
  },
  red: {
    color: 'chip.red',
    deleteIcon: {
      opacity: 0.7,
    },
  },
  green: {
    color: 'chip.green',
    deleteIcon: {
      opacity: 0.7,
    },
  },
  gray: {
    color: 'chip.gray',
    deleteIcon: {
      opacity: 0.7,
    },
  },
  purple: {
    color: 'chip.purple',
    deleteIcon: {
      opacity: 0.7,
    },
  },
};

const outlinedColorMap: Record<
  NonNullable<LookoChipOutlinedProps['color']>,
  {
    color: NonNullable<ChipProps['color']>;
    deleteIcon: {
      opacity: number;
    };
  }
> = {
  primary: {
    color: 'chip.outlined.primary',
    deleteIcon: {
      opacity: 0.7,
    },
  },
  secondary: {
    color: 'chip.outlined.secondary',
    deleteIcon: {
      opacity: 0.26,
    },
  },
};

/**
 * Chip 컴포넌트.
 *
 * TODO(#2076):
 * - hover 색상 정의
 */
export const LookoChip = styled(
  ({ label, color = 'primary', variant = 'filled', showDeleteIcon = false, ...props }: LookoChipProps) => {
    return (
      <Chip
        className="LookoChip"
        label={
          <HStack alignItems={'center'}>
            <Typography variant="Body_Looko" px={'6px'}>
              {label}
            </Typography>
            {showDeleteIcon && (
              <Cancel
                fontSize="small"
                sx={{
                  opacity: (variant === 'filled'
                    ? filledColorMap[color]
                    : outlinedColorMap[color as NonNullable<LookoChipOutlinedProps['color']>]
                  ).deleteIcon.opacity,
                }}
              />
            )}
          </HStack>
        }
        color={
          (variant === 'filled'
            ? filledColorMap[color]
            : outlinedColorMap[color as NonNullable<LookoChipOutlinedProps['color']>]
          ).color
        }
        {...props}
      />
    );
  },
)(({ theme, color = 'primary', variant = 'filled' }) => {
  return {
    height: 28,
    [`& .${chipClasses.label}`]: {
      paddingLeft: 4,
      paddingRight: 4,
    },

    ...(color === 'white'
      ? {
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: '#0000001F',
        }
      : {}),

    ...(variant === 'outlined'
      ? {
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: (
            theme.palette[
              outlinedColorMap[color as NonNullable<LookoChipOutlinedProps['color']>].color as keyof Palette
            ] as PaletteColor | undefined
          )?.main,
          backgroundColor: 'transparent',
        }
      : {}),
  };
});
